import { useRef, useState, useEffect } from "react";
import VirtualList from "react-tiny-virtual-list";
import { PricedBond } from "../PricedBond";
import { BondCardCompact } from "../card/BondCardCompact";
import { BondCardWide } from "../card/BondCardWide";

export const VirtualizedBondsList = ({ wide, items }: { wide: boolean; items: PricedBond[] }) => {
  const refContainer = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [screenWidth, setScreenWidth] = useState(0);

  useEffect(() => {
    function handleResize() {
      const { width } = refContainer.current?.getBoundingClientRect() ?? {
        width: 0,
      };

      setWidth(Math.floor(width));
      setScreenWidth(Math.floor(window.innerWidth));
    }
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const MAGIC_CONSTANT_TO_ADJUST_GAP = 4;

  const GAP_HORIZONTAL = 28 + MAGIC_CONSTANT_TO_ADJUST_GAP;
  const GAP_VERTICAL = 28;

  const NON_WIDE_NORMAL_WIDTH = 309 + GAP_HORIZONTAL;

  const NON_WIDE_HEIGHT = 513 + GAP_VERTICAL;
  const WIDE_GREATER_THAN_1280_HEIGHT = 302 + GAP_VERTICAL;
  const WIDE_LESS_THAN_1280_HEIGHT = 451 + GAP_VERTICAL;
  const WIDE_LESS_THAN_995_HEIGHT = 697 + GAP_VERTICAL;
  const WIDE_LESS_THAN_767_HEIGHT = 817 + GAP_VERTICAL;
  const WIDE_LESS_THAN_640_HEIGHT = 1317 + GAP_VERTICAL;
  const WIDE_LESS_THAN_320_HEIGHT = 1284 + GAP_VERTICAL;

  const itemSize = wide
    ? screenWidth >= 1280
      ? WIDE_GREATER_THAN_1280_HEIGHT
      : screenWidth > 995
      ? WIDE_LESS_THAN_1280_HEIGHT
      : screenWidth > 767
      ? WIDE_LESS_THAN_995_HEIGHT
      : screenWidth > 640
      ? WIDE_LESS_THAN_767_HEIGHT
      : screenWidth > 319
      ? WIDE_LESS_THAN_640_HEIGHT
      : WIDE_LESS_THAN_320_HEIGHT
    : NON_WIDE_HEIGHT;

  const itemsOnRow = Math.max(Math.floor(width / NON_WIDE_NORMAL_WIDTH), 1);
  const numberOfRows = wide ? items.length : Math.ceil(items.length / itemsOnRow);

  const SUM_TOP_BAR_HEIGHT = 300;

  return (
    <div ref={refContainer}>
      <VirtualList
        height={`calc(100vh - ${SUM_TOP_BAR_HEIGHT}px)`}
        width={width}
        itemCount={numberOfRows}
        itemSize={itemSize}
        className="custom-scrollbar px-10"
        renderItem={({ index, style }) => {
          if (wide) {
            const item = items[index];

            return (
              <div
                style={style}
                key={`${item.bondIdCusip}`}
                className="flex flex-row w-full justify-center items-start"
              >
                <BondCardWide item={item} />
              </div>
            );
          }

          const firstItem = items[index * itemsOnRow];

          return (
            <div
              style={style}
              key={`${firstItem.bondIdCusip}`}
              className="flex flex-row w-full justify-center items-start"
            >
              {Array(itemsOnRow)
                .fill(0)
                .map((_, i) => {
                  const item = items[index * itemsOnRow + i];

                  if (item === undefined) {
                    return null;
                  }

                  return (
                    <div
                      key={`${item.bondIdCusip}`}
                      style={{ width: `${NON_WIDE_NORMAL_WIDTH}px` }}
                      className="flex flex-row w-full justify-center items-start"
                    >
                      <BondCardCompact item={item} />
                    </div>
                  );
                })}
            </div>
          );
        }}
      />
    </div>
  );
};
